/* eslint-disable @typescript-eslint/no-use-before-define */
import { useLang } from 'hooks/lang';

import { actions, invoiceConfigByCode } from 'entities';

import { Button, Column, Label, LabeledTextarea } from 'components/Blocks';
import { SingleFileUploadButton } from 'components/Uploads';
import { CircleDollarIcon } from 'components/Icons';

import { usePackageValueChange } from './changeValue.context';
import { usePackage } from '../../context';

import { formatCurrency } from 'utils/text';
import { pick } from 'utils/object';
import { getID } from 'utils/id';
import { useUser } from 'hooks/user';
import { MonetaryInput } from 'components/Inputs';

function sumValues(entries: Array<{ value: string }>): number {
  const total = entries.reduce((acc, next) => acc + Number(next.value), 0);

  return Number(Number(total).toFixed(2));
}

export function ChangeValueForm(): JSX.Element {
  const { t } = useLang();
  const user = useUser();

  const { data, overwrite } = usePackage();
  const { handleChange, state, dispatch, close } = usePackageValueChange();

  const minValue = Math.max(
    ...[
      data.projects,
      data.invoices.filter((i) => invoiceConfigByCode[i.status].countTowardsPackageUsage),
    ].map(sumValues),
  );

  const canSubmit = [
    Number(state.value) >= minValue,
    state.justification.trim(),
    state.proofFile,
  ].every(Boolean);

  const changeValue = actions.invoice.changePackageValue.use({
    block: !canSubmit,

    params: {
      ...pick(state, ['justification', 'value']),
      packageId: data.id,
      proofFile: state.proofFile!,
    },

    onSuccess: () => {
      overwrite((old) => ({
        ...old,
        value: state.value,
        valueChanges: [
          {
            ...pick(state, ['justification', 'value']),
            currency: data.currency,
            doneBy: user.id,
            file: state.proofFile!,
            id: getID(),
            oldValue: data.value,
            packageId: data.id,
            timestamp: new Date().toISOString(),
          },
          ...old.valueChanges,
        ],
      }));

      close();
    },
  });

  return (
    <Column gap="6">
      <Column gap="2">
        <MonetaryInput
          label={t('new-value')}
          currency={data.currency}
          currencyDisabled
          name="value"
          onChange={handleChange}
        />

        <span className="text-xs">
          {t('min-value-description')} <strong>{formatCurrency(minValue, data.currency)}</strong>
        </span>
      </Column>

      <Column gap="2">
        <Label>{t('proof-file')}</Label>

        <SingleFileUploadButton
          file={state.proofFile}
          setFile={(proofFile) => dispatch({ proofFile })}
          uploadPrefix="packages"
        />
      </Column>

      <LabeledTextarea
        name="justification"
        value={state.justification}
        onChange={handleChange}
        label={t('justification')!}
      />

      <Button disabled={!canSubmit} variant="outlined" {...changeValue.buttonProps} size="medium">
        <CircleDollarIcon />
        {t('change-value')}
      </Button>
    </Column>
  );
}
